/**
 * Frontend Reporting Constants
 * Mirrors backend/src/utils/reportingConstants.js
 * Based on: Aktivity Report Interpretation Working Documents (18 pages)
 */

// ==================== URGENCY WEIGHTS ====================
export const URGENCY_WEIGHTS = {
  '7_days': 1.0,
  '15_days': 0.85,
  '30_days': 0.70,
  '60_days': 0.45,
  '90_days': 0.25,
  'not_sure': 0.15,
};

// Human-readable labels for by-when options
export const BY_WHEN_LABELS = {
  '7_days': 'Within 7 days',
  '15_days': 'Within 15 days',
  '30_days': 'Within 30 days',
  '60_days': 'Within 60 days',
  '90_days': 'Within 90 days',
  'not_sure': 'Not sure yet',
};

// ==================== SCORE BANDS ====================

// Action Readiness Score (ARS) - 0.0 to 1.0 (3 bands as per PDF Page 3)
export const ARS_BANDS = {
  HIGH: { min: 0.70, max: 1.0, label: 'High Readiness', color: 'green' },
  MODERATE: { min: 0.40, max: 0.69, label: 'Moderate Readiness', color: 'yellow' },
  LOW: { min: 0, max: 0.39, label: 'Low Readiness', color: 'red' },
};

// Topic Relevance Score (TRS) - 1.0 to 5.0 (5 bands as per PDF Page 11-12)
export const TRS_BANDS = {
  EXTREMELY_RELEVANT: { min: 4.50, max: 5.00, label: 'Extremely Relevant', color: 'green' },
  HIGHLY_RELEVANT: { min: 4.00, max: 4.49, label: 'Highly Relevant', color: 'green' },
  MOSTLY_RELEVANT: { min: 3.50, max: 3.99, label: 'Mostly Relevant', color: 'yellow' },
  MODERATELY_RELEVANT: { min: 3.00, max: 3.49, label: 'Moderately Relevant', color: 'yellow' },
  LOW_RELEVANCE: { min: 0, max: 2.99, label: 'Low Relevance', color: 'red' },
};

// Trainer Effectiveness Index (TEI) - 1.0 to 5.0 (5 bands as per PDF Page 13-14)
export const TEI_BANDS = {
  EXCEPTIONAL: { min: 4.50, max: 5.00, label: 'Exceptional', color: 'green' },
  HIGHLY_EFFECTIVE: { min: 4.00, max: 4.49, label: 'Highly Effective', color: 'green' },
  EFFECTIVE_INCONSISTENT: { min: 3.50, max: 3.99, label: 'Effective but Inconsistent', color: 'yellow' },
  MODERATELY_EFFECTIVE: { min: 3.00, max: 3.49, label: 'Moderately Effective', color: 'yellow' },
  LOW_EFFECTIVENESS: { min: 0, max: 2.99, label: 'Low Perceived Effectiveness', color: 'red' },
};

// Participant Confidence Signal (PCS) - 1.0 to 5.0 (3 bands as per PDF Page 7-8)
export const PCS_BANDS = {
  HIGH: { min: 4.00, max: 5.00, label: 'High Confidence', color: 'green' },
  MODERATE: { min: 3.00, max: 3.99, label: 'Moderate Confidence', color: 'yellow' },
  LOW: { min: 0, max: 2.99, label: 'Low Confidence', color: 'red' },
};

// Learning Advocacy Index (LAI) - 1.0 to 5.0 (3 bands as per PDF Page 16)
export const LAI_BANDS = {
  HIGH: { min: 4.00, max: 5.00, label: 'High Advocacy', color: 'green' },
  MODERATE: { min: 3.00, max: 3.99, label: 'Moderate Advocacy', color: 'yellow' },
  LOW: { min: 0, max: 2.99, label: 'Low Advocacy', color: 'red' },
};

// Challenge Resolution Index (CRI) - 1.0 to 5.0 (3 bands as per PDF Page 17-18)
export const CRI_BANDS = {
  HIGH: { min: 4.00, max: 5.00, label: 'High Resolution', color: 'green' },
  MODERATE: { min: 3.00, max: 3.99, label: 'Moderate Resolution', color: 'yellow' },
  LOW: { min: 0, max: 2.99, label: 'Low Resolution', color: 'red' },
};

// Learning Implementation Indicator (LII) - 1.0 to 5.0 (5 bands as per PDF Page 10)
export const LII_BANDS = {
  CLEAR_COMMITTED: { min: 4.5, max: 5.0, label: 'Clear & Committed', color: 'green' },
  ACTIONABLE_SUPPORT: { min: 3.8, max: 4.4, label: 'Actionable with Minor Support', color: 'green' },
  INTENT_UNCLEAR: { min: 3.0, max: 3.7, label: 'Intent Present, Action Unclear', color: 'yellow' },
  LOW_READINESS: { min: 2.0, max: 2.9, label: 'Low Action Readiness', color: 'orange' },
  NO_COMMITMENT: { min: 1.0, max: 1.9, label: 'No Clear Action Commitment', color: 'red' },
};

// ==================== BAND LOOKUP FUNCTIONS ====================

export const getARSBand = (score) => {
  if (score >= ARS_BANDS.HIGH.min) return ARS_BANDS.HIGH;
  if (score >= ARS_BANDS.MODERATE.min) return ARS_BANDS.MODERATE;
  return ARS_BANDS.LOW;
};

export const getTRSBand = (score) => {
  if (score >= TRS_BANDS.EXTREMELY_RELEVANT.min) return TRS_BANDS.EXTREMELY_RELEVANT;
  if (score >= TRS_BANDS.HIGHLY_RELEVANT.min) return TRS_BANDS.HIGHLY_RELEVANT;
  if (score >= TRS_BANDS.MOSTLY_RELEVANT.min) return TRS_BANDS.MOSTLY_RELEVANT;
  if (score >= TRS_BANDS.MODERATELY_RELEVANT.min) return TRS_BANDS.MODERATELY_RELEVANT;
  return TRS_BANDS.LOW_RELEVANCE;
};

export const getTEIBand = (score) => {
  if (score >= TEI_BANDS.EXCEPTIONAL.min) return TEI_BANDS.EXCEPTIONAL;
  if (score >= TEI_BANDS.HIGHLY_EFFECTIVE.min) return TEI_BANDS.HIGHLY_EFFECTIVE;
  if (score >= TEI_BANDS.EFFECTIVE_INCONSISTENT.min) return TEI_BANDS.EFFECTIVE_INCONSISTENT;
  if (score >= TEI_BANDS.MODERATELY_EFFECTIVE.min) return TEI_BANDS.MODERATELY_EFFECTIVE;
  return TEI_BANDS.LOW_EFFECTIVENESS;
};

export const getPCSBand = (score) => {
  if (score >= PCS_BANDS.HIGH.min) return PCS_BANDS.HIGH;
  if (score >= PCS_BANDS.MODERATE.min) return PCS_BANDS.MODERATE;
  return PCS_BANDS.LOW;
};

export const getLAIBand = (score) => {
  if (score >= LAI_BANDS.HIGH.min) return LAI_BANDS.HIGH;
  if (score >= LAI_BANDS.MODERATE.min) return LAI_BANDS.MODERATE;
  return LAI_BANDS.LOW;
};

export const getCRIBand = (score) => {
  if (score >= CRI_BANDS.HIGH.min) return CRI_BANDS.HIGH;
  if (score >= CRI_BANDS.MODERATE.min) return CRI_BANDS.MODERATE;
  return CRI_BANDS.LOW;
};

export const getLIIBand = (score) => {
  if (score >= LII_BANDS.CLEAR_COMMITTED.min) return LII_BANDS.CLEAR_COMMITTED;
  if (score >= LII_BANDS.ACTIONABLE_SUPPORT.min) return LII_BANDS.ACTIONABLE_SUPPORT;
  if (score >= LII_BANDS.INTENT_UNCLEAR.min) return LII_BANDS.INTENT_UNCLEAR;
  if (score >= LII_BANDS.LOW_READINESS.min) return LII_BANDS.LOW_READINESS;
  return LII_BANDS.NO_COMMITMENT;
};

// ==================== FEEDBACK QUESTIONS ====================

export const FEEDBACK_QUESTIONS = {
  Q1: {
    key: 'topicRelevance',
    question: 'How relevant and useful was this topic for your role?',
    metric: 'Topic Relevance Score (TRS)',
    scale: { min: 1, max: 5 },
  },
  Q2: {
    key: 'trainerEffectiveness',
    question: 'How effectively did the trainer help you understand and engage with this topic?',
    metric: 'Trainer Effectiveness Index (TEI)',
    scale: { min: 1, max: 5 },
  },
  Q3: {
    key: 'confidenceToApply',
    question: 'How confident do you feel about applying this topic to overcome real work challenges?',
    metric: 'Participant Confidence Signal (PCS)',
    scale: { min: 1, max: 5 },
  },
  Q4: {
    key: 'learningAdvocacy',
    question: 'How likely are you to apply this learning and recommend it to others?',
    metric: 'Learning Advocacy Index (LAI) / Learning Implementation Indicator (LII)',
    scale: { min: 1, max: 5 },
  },
  Q5: {
    key: 'challengeResolution',
    question: 'To what extent did this topic address a real challenge you are currently facing?',
    metric: 'Challenge Resolution Index (CRI)',
    scale: { min: 1, max: 5 },
  },
};

// ==================== METRIC DESCRIPTIONS ====================

export const METRIC_DESCRIPTIONS = {
  ars: {
    name: 'Action Readiness Score',
    shortName: 'ARS',
    description: 'Measures how likely a participant is to take action based on urgency and commitment.',
    formula: 'ARS = Urgency Weight × Commitment Factor',
    maxValue: 1.0,
    icon: 'rocket'
  },
  trs: {
    name: 'Topic Relevance Score',
    shortName: 'TRS',
    description: 'How relevant participants found the training content to their work.',
    formula: 'Average of Q1 responses',
    maxValue: 5.0,
    icon: 'target'
  },
  tei: {
    name: 'Trainer Effectiveness Index',
    shortName: 'TEI',
    description: 'How effective participants found the trainer in delivering the content.',
    formula: 'Average of Q2 responses',
    maxValue: 5.0,
    icon: 'user'
  },
  pcs: {
    name: 'Participant Confidence Signal',
    shortName: 'PCS',
    description: 'How confident participants feel about applying what they learned.',
    formula: 'Average of Q3 responses',
    maxValue: 5.0,
    icon: 'shield'
  },
  lai: {
    name: 'Learning Advocacy Index',
    shortName: 'LAI',
    description: 'How likely participants are to recommend this training to others.',
    formula: 'Average of Q4 responses',
    maxValue: 5.0,
    icon: 'megaphone'
  },
  cri: {
    name: 'Challenge Resolution Index',
    shortName: 'CRI',
    description: 'How well the training addressed participant challenges.',
    formula: 'Average of Q5 responses',
    maxValue: 5.0,
    icon: 'check-circle'
  },
  lii: {
    name: 'Learning Implementation Indicator',
    shortName: 'LII',
    description: 'Individual likelihood of implementing learning (same data as LAI, different context).',
    formula: 'Individual Q4 response',
    maxValue: 5.0,
    icon: 'play'
  }
};

// ==================== INTERPRETATION TEXTS (from PDF) ====================

// ARS Interpretations (Page 3-5)
export const ARS_INTERPRETATIONS = {
  participant: {
    high: 'You are clear and confident about applying what you learned. You know what to do next and how this learning fits into your work.',
    moderate: 'You understand the learning, but need a bit more clarity or practice before acting. Revisiting examples or discussing with your manager will help you move forward.',
    low: 'You may be unclear about how to apply this learning to your role right now. Reflecting on your work challenges or seeking guidance can help turn learning into action.',
  },
  trainer: {
    high: 'Participants demonstrate strong readiness to implement their action plans. They have clear timelines and high commitment levels.',
    moderate: 'Participants show intent to act but may need additional support or clarity. Consider follow-up touchpoints to reinforce action.',
    low: 'Participants are uncertain about implementation. Review whether action planning guidance was sufficient and if realistic timelines were set.',
  },
  client: {
    high: 'Strong action readiness across sessions indicates effective translation of learning into planned actions. Expect high implementation rates.',
    moderate: 'Moderate action readiness suggests participants understand the content but may need organizational support to implement.',
    low: 'Low action readiness signals a gap between learning and planned action. Consider reviewing session content relevance or providing post-session support.',
  },
};

// PCS Interpretations (Page 7-8)
export const PCS_INTERPRETATIONS = {
  participant: {
    high: 'You feel ready to apply this topic confidently in your work.',
    moderate: 'You see value in this topic but your confidence is still forming. Application may take some practice.',
    low: 'You are not yet confident applying this topic. Consider seeking additional practice or examples.',
  },
  trainer: {
    high: 'Participants feel strongly equipped and capable of handling their challenges. The trainer\'s delivery, examples, and practice opportunities built real confidence.',
    moderate: 'Participants gained awareness but still lack full confidence to act independently. More contextual examples, role practice, or reinforcement would strengthen confidence.',
    low: 'Participants do not yet feel confident applying the learning to their challenges. This suggests gaps in relevance, clarity, or practical application during the session.',
  },
  client: {
    high: 'Learning programs are successfully building participant readiness to act. This indicates strong alignment between training content, delivery, and real business challenges.',
    moderate: 'Learning programs are creating awareness and intent, but confidence to act is uneven. This may vary by role, topic, or business context.',
    low: 'Learning programs are not yet translating into participant readiness to act. This may indicate issues with relevance, application focus, or learning design.',
  },
};

// TRS Interpretations (Page 11-12) - Trainer/Client only
export const TRS_INTERPRETATIONS = {
  trainer: {
    extremely_relevant: 'Participants strongly resonated with this topic and clearly saw its applicability to their work.',
    highly_relevant: 'The topic was well-received and felt relevant for most participants.',
    mostly_relevant: 'The topic made sense to participants, but relevance was not consistent across the group.',
    moderately_relevant: 'Participant perception of relevance was divided. Consider tailoring examples to specific roles.',
    low_relevance: 'Participants struggled to see the relevance of this topic to their work. Review content alignment with audience needs.',
  },
  client: {
    extremely_relevant: 'Sessions are highly aligned with participant needs. Content strategy is effective.',
    highly_relevant: 'Good alignment between training content and participant roles. Minor adjustments may optimize further.',
    mostly_relevant: 'Content is generally relevant but not uniformly applicable. Consider role-specific customization.',
    moderately_relevant: 'Mixed relevance perception suggests content may need better alignment with participant challenges.',
    low_relevance: 'Low relevance scores indicate a significant gap between training content and actual work needs. Strategic content review recommended.',
  },
};

// TEI Interpretations (Page 13-14)
export const TEI_INTERPRETATIONS = {
  trainer: {
    exceptional: 'Participants experienced you as highly effective in facilitation, clarity, and engagement. Excellent delivery.',
    highly_effective: 'You delivered the session effectively and met participant expectations. Strong performance.',
    effective_inconsistent: 'Your effectiveness was generally positive but varied across participants. Review areas for consistency.',
    moderately_effective: 'Your delivery met basic expectations but did not strongly engage all participants. Consider varying techniques.',
    low_effectiveness: 'Participants struggled to connect with your delivery style. Reflect on engagement techniques and seek feedback.',
  },
  client: {
    exceptional: 'Trainer demonstrates exceptional facilitation skills. Consider for advanced or critical sessions.',
    highly_effective: 'Trainer consistently delivers effective sessions. Good match for most training needs.',
    effective_inconsistent: 'Trainer effectiveness varies. May benefit from coaching on specific delivery aspects.',
    moderately_effective: 'Trainer meets basic standards but has room for improvement. Consider development support.',
    low_effectiveness: 'Trainer effectiveness is below expectations. Coaching or role reassessment may be needed.',
  },
};

// LAI Interpretations (Page 16-17)
export const LAI_INTERPRETATIONS = {
  participant: {
    high: 'You are likely to champion this learning with your peers and actively apply it.',
    moderate: 'You see value in the learning but may not actively promote it to others.',
    low: 'You are unlikely to recommend this learning to others. It may not have resonated with your needs.',
  },
  trainer: {
    high: 'Participants actively advocate for sessions delivered by this trainer. They perceive strong value, credibility, and relevance.',
    moderate: 'Participants are generally satisfied but selectively advocate. The learning experience met expectations, but did not strongly stand out.',
    low: 'Participants are hesitant to recommend sessions delivered by this trainer. This suggests gaps in perceived value, relevance, or overall experience.',
  },
  client: {
    high: 'Learning programs are seen as valuable and worth recommending. This indicates a healthy learning culture and strong participant buy-in.',
    moderate: 'Learning programs are viewed as useful, but not yet compelling enough to drive strong advocacy.',
    low: 'Learning programs are not seen as advocacy-worthy by participants. This may impact future participation, engagement, and learning credibility.',
  },
};

// CRI Interpretations (Page 17-18)
export const CRI_INTERPRETATIONS = {
  participant: {
    high: 'This topic directly addressed challenges you are facing at work.',
    moderate: 'This topic somewhat addressed your work challenges. Some aspects may need further exploration.',
    low: 'This topic did not significantly address your current work challenges.',
  },
  trainer: {
    high: 'Participants feel the session clearly helped them address or move closer to resolving their real work challenges.',
    moderate: 'Participants gained useful insights, but full challenge resolution may require further reflection or application.',
    low: 'Participants did not strongly feel that the session helped with their real challenges.',
  },
  client: {
    high: 'Learning programs are effectively addressing real business challenges faced by participants.',
    moderate: 'Learning programs are partially helping participants address challenges, but impact is uneven.',
    low: 'Learning programs are not yet perceived as helpful in addressing real workplace challenges.',
  },
};

// LII Interpretations (Page 9-10)
export const LII_INTERPRETATIONS = {
  participant: {
    clear_committed: 'You are very likely to implement this learning in your work. Your confidence suggests strong clarity, relevance, and readiness to act.',
    actionable_support: 'You feel confident applying this learning, with some need for reinforcement or practice in real situations.',
    intent_unclear: 'You see value in the learning but may need clearer application steps or support to translate it into action.',
    low_readiness: 'You may face challenges applying this learning without additional guidance, examples, or workplace support.',
    no_commitment: 'You currently feel unable to apply this learning in your role or context.',
  },
  trainer: {
    clear_committed: 'Participants have clear implementation plans and high commitment.',
    actionable_support: 'Participants understand the path forward with minor support needed.',
    intent_unclear: 'Participants show intent but need help clarifying action steps.',
    low_readiness: 'Low implementation readiness. Consider strengthening action planning portion of session.',
    no_commitment: 'Participants have not committed to action. Review relevance and practical applicability.',
  },
  client: {
    clear_committed: 'Strong implementation readiness indicates learning will translate to behavior change.',
    actionable_support: 'Good implementation potential with organizational support.',
    intent_unclear: 'Intent exists but action clarity is lacking. Consider post-session support programs.',
    low_readiness: 'Low implementation readiness may limit learning ROI. Review content practicality.',
    no_commitment: 'No clear action commitment signals systemic issues with learning relevance or support.',
  },
};

// ==================== HELPER FUNCTIONS ====================

/**
 * Calculate ARS from byWhen and commitment
 */
export const calculateARS = (byWhen, commitment) => {
  const UW = URGENCY_WEIGHTS[byWhen] || URGENCY_WEIGHTS['not_sure'];
  const CF = (commitment || 1) / 10;
  return Math.round((UW * CF) * 100) / 100;
};

/**
 * Get metric band and color for a value
 */
export const getMetricBandInfo = (metricType, value) => {
  const getBandFn = {
    ars: getARSBand,
    trs: getTRSBand,
    tei: getTEIBand,
    tes: getTEIBand, // alias
    pcs: getPCSBand,
    pci: getPCSBand, // alias
    lai: getLAIBand,
    cri: getCRIBand,
    lii: getLIIBand
  };

  const fn = getBandFn[metricType.toLowerCase()];
  if (fn) {
    return fn(value);
  }
  return { label: 'Unknown', color: 'gray' };
};

/**
 * Get ARS interpretation text
 */
export const getARSInterpretation = (score, level = 'participant') => {
  const band = getARSBand(score);
  const interpretations = ARS_INTERPRETATIONS[level];
  if (band === ARS_BANDS.HIGH) return interpretations.high;
  if (band === ARS_BANDS.MODERATE) return interpretations.moderate;
  return interpretations.low;
};

/**
 * Get PCS interpretation text
 */
export const getPCSInterpretation = (score, level = 'participant') => {
  const band = getPCSBand(score);
  const interpretations = PCS_INTERPRETATIONS[level];
  if (band === PCS_BANDS.HIGH) return interpretations.high;
  if (band === PCS_BANDS.MODERATE) return interpretations.moderate;
  return interpretations.low;
};

/**
 * Get TRS interpretation text
 */
export const getTRSInterpretation = (score, level = 'trainer') => {
  const band = getTRSBand(score);
  const interpretations = TRS_INTERPRETATIONS[level];
  if (band === TRS_BANDS.EXTREMELY_RELEVANT) return interpretations.extremely_relevant;
  if (band === TRS_BANDS.HIGHLY_RELEVANT) return interpretations.highly_relevant;
  if (band === TRS_BANDS.MOSTLY_RELEVANT) return interpretations.mostly_relevant;
  if (band === TRS_BANDS.MODERATELY_RELEVANT) return interpretations.moderately_relevant;
  return interpretations.low_relevance;
};

/**
 * Get TEI interpretation text
 */
export const getTEIInterpretation = (score, level = 'trainer') => {
  const band = getTEIBand(score);
  const interpretations = TEI_INTERPRETATIONS[level];
  if (band === TEI_BANDS.EXCEPTIONAL) return interpretations.exceptional;
  if (band === TEI_BANDS.HIGHLY_EFFECTIVE) return interpretations.highly_effective;
  if (band === TEI_BANDS.EFFECTIVE_INCONSISTENT) return interpretations.effective_inconsistent;
  if (band === TEI_BANDS.MODERATELY_EFFECTIVE) return interpretations.moderately_effective;
  return interpretations.low_effectiveness;
};

/**
 * Get LAI interpretation text
 */
export const getLAIInterpretation = (score, level = 'participant') => {
  const band = getLAIBand(score);
  const interpretations = LAI_INTERPRETATIONS[level];
  if (band === LAI_BANDS.HIGH) return interpretations.high;
  if (band === LAI_BANDS.MODERATE) return interpretations.moderate;
  return interpretations.low;
};

/**
 * Get CRI interpretation text
 */
export const getCRIInterpretation = (score, level = 'participant') => {
  const band = getCRIBand(score);
  const interpretations = CRI_INTERPRETATIONS[level];
  if (band === CRI_BANDS.HIGH) return interpretations.high;
  if (band === CRI_BANDS.MODERATE) return interpretations.moderate;
  return interpretations.low;
};

/**
 * Get LII interpretation text
 */
export const getLIIInterpretation = (score, level = 'participant') => {
  const band = getLIIBand(score);
  const interpretations = LII_INTERPRETATIONS[level];
  if (band === LII_BANDS.CLEAR_COMMITTED) return interpretations.clear_committed;
  if (band === LII_BANDS.ACTIONABLE_SUPPORT) return interpretations.actionable_support;
  if (band === LII_BANDS.INTENT_UNCLEAR) return interpretations.intent_unclear;
  if (band === LII_BANDS.LOW_READINESS) return interpretations.low_readiness;
  return interpretations.no_commitment;
};

/**
 * Get metric description
 */
export const getMetricDescription = (metricType) => {
  return METRIC_DESCRIPTIONS[metricType.toLowerCase()] || null;
};

export default {
  URGENCY_WEIGHTS,
  BY_WHEN_LABELS,
  ARS_BANDS,
  TRS_BANDS,
  TEI_BANDS,
  PCS_BANDS,
  LAI_BANDS,
  CRI_BANDS,
  LII_BANDS,
  FEEDBACK_QUESTIONS,
  METRIC_DESCRIPTIONS,
  ARS_INTERPRETATIONS,
  PCS_INTERPRETATIONS,
  TRS_INTERPRETATIONS,
  TEI_INTERPRETATIONS,
  LAI_INTERPRETATIONS,
  CRI_INTERPRETATIONS,
  LII_INTERPRETATIONS,
  getARSBand,
  getTRSBand,
  getTEIBand,
  getPCSBand,
  getLAIBand,
  getCRIBand,
  getLIIBand,
  getARSInterpretation,
  getPCSInterpretation,
  getTRSInterpretation,
  getTEIInterpretation,
  getLAIInterpretation,
  getCRIInterpretation,
  getLIIInterpretation,
  calculateARS,
  getMetricBandInfo,
  getMetricDescription
};
